import React, { useState, useRef, useEffect } from 'react';

const ERROR_IMG_SRC =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4KCg==';

interface ImageWithFallbackProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  lazy?: boolean;
  aspectRatio?: string; // e.g., "16/9", "1/1", "4/3"
}

export function ImageWithFallback(props: ImageWithFallbackProps) {
  const [didError, setDidError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(!props.lazy);
  const imgRef = useRef<HTMLImageElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!props.lazy || isInView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.01, // Start loading as soon as 1% is visible
        rootMargin: '100px 50px', // Load images 100px before they come into view
      }
    );

    const currentRef = imgRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [props.lazy, isInView]);

  const handleError = () => {
    setDidError(true);
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const { src, alt, style, className, lazy, aspectRatio, ...rest } = props;

  // Calculate container style with aspect ratio
  const containerStyle = {
    ...style,
    aspectRatio: aspectRatio || 'auto',
  };

  // Show placeholder while not in view or loading
  if (!isInView) {
    return (
      <div
        ref={imgRef}
        className={`bg-gray-200 animate-pulse ${className ?? ''}`}
        style={containerStyle}
        role="img"
        aria-label={alt || 'Loading image'}
        {...rest}
      />
    );
  }

  return didError ? (
    <div
      className={`inline-block bg-gray-100 text-center align-middle ${className ?? ''}`}
      style={containerStyle}
    >
      <div className="flex items-center justify-center w-full h-full">
        <img
          src={ERROR_IMG_SRC}
          alt="Error loading image"
          width="88"
          height="88"
          {...rest}
          data-original-url={src}
        />
      </div>
    </div>
  ) : (
    <div style={containerStyle} className={`relative ${className ?? ''}`}>
      {/* Background placeholder that stays until image loads */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-lg"></div>
      )}

      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`${
          !isLoaded ? 'opacity-0' : 'opacity-100'
        } transition-opacity duration-500 ease-out w-full h-full object-cover`}
        style={{ aspectRatio: aspectRatio || 'auto' }}
        loading="lazy"
        decoding="async"
        {...rest}
        onError={handleError}
        onLoad={handleLoad}
      />
    </div>
  );
}
